import { z } from "zod"

import { entityIdSchema } from "./identifiers.js"
import { payrollDataPurposeV1Schema } from "./payroll-data-governance-v1.js"

const providerIdSchema = z.string().regex(/^[A-Za-z0-9_.:-]{1,128}$/)
const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
const moneySchema = z.string().regex(/^(0|[1-9]\d{0,15})\.\d{2}$/)

export const contractorResultAccessActionV1Schema = z.enum([
  "view",
  "download_1099",
])

export const contractorPaymentResultAccessV1Schema = z
  .object({
    accessEventId: entityIdSchema,
    runId: entityIdSchema,
    payrollInputId: entityIdSchema,
    payrollProfileId: entityIdSchema,
    providerPaystubId: providerIdSchema,
    providerForm1099Id: providerIdSchema.nullable(),
    taxYear: z.number().int().min(2000).max(2200),
    action: contractorResultAccessActionV1Schema,
    purpose: payrollDataPurposeV1Schema,
    stepUpAssertion: z.string().min(100).max(4096),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.action === "download_1099" && value.providerForm1099Id === null) {
      context.addIssue({
        code: "custom",
        message: "A 1099 form reference is required for download.",
        path: ["providerForm1099Id"],
      })
    }
  })

export const contractorPaymentResultV1Schema = z
  .object({
    entityType: z.literal("contractor_payment_result"),
    entityId: entityIdSchema,
    state: z.enum(["processing", "settled", "failed"]),
    version: z.literal(1),
    accessEventId: entityIdSchema,
    runId: entityIdSchema,
    payrollInputId: entityIdSchema,
    workerId: entityIdSchema,
    payableKind: z.enum(["contractor_hourly", "contractor_job"]),
    approvedMinutes: z.number().int().positive().nullable(),
    approvedAmount: moneySchema.nullable(),
    payPeriodStart: dateSchema,
    payPeriodEnd: dateSchema,
    payDate: dateSchema.nullable(),
    currency: z.literal("USD"),
    providerGrossAmount: moneySchema,
    providerNetAmount: moneySchema,
    form1099Status: z.enum(["unavailable", "available"]),
    form1099Year: z.number().int().min(2000).max(2200),
    authoritativeSource: z.literal("Worklio"),
    recalculated: z.literal(false),
    accessAudited: z.literal(true),
    downloadAuthorized: z.boolean(),
  })
  .strict()

export type ContractorPaymentResultAccessV1 = z.infer<
  typeof contractorPaymentResultAccessV1Schema
>
export type ContractorPaymentResultV1 = z.infer<
  typeof contractorPaymentResultV1Schema
>
